'use client';

import { ArrowRight, Trash2 } from 'lucide-react';
import { formatPrice } from '@/utils/formatters';
import Link from 'next/link';
import { useState } from 'react';
import { useCartActions } from '@/core/application/providers/StateMachineProvider';

interface CartPreviewFooterProps {
  total: number;
  isEmpty: boolean;
  isLoading: boolean;
  hasError: boolean;
  onOrderNowClick: () => void;
}

export function CartPreviewFooter({ total, isEmpty, isLoading, hasError, onOrderNowClick }: CartPreviewFooterProps) {
  const { clearCart } = useCartActions();
  const [showConfirmation, setShowConfirmation] = useState(false);

  if (isEmpty || isLoading || hasError) {
    return null;
  }

  const handleClearCart = () => {
    setShowConfirmation(true);
  };

  const confirmClearCart = () => {
    clearCart();
    setShowConfirmation(false);
  };

  const cancelClearCart = () => {
    setShowConfirmation(false);
  };

  return (
    <div className="px-4 py-3">
      <div className="flex text-md justify-end font-medium text-gray-800 mb-4">Subtotal: {formatPrice(total)}</div>

      {showConfirmation ? (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800 mb-3">Are you sure you want to clear all items from your cart?</p>
          <div className="flex gap-2">
            <button
              onClick={confirmClearCart}
              className="flex-1 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors">
              Yes, Clear Cart
            </button>
            <button
              onClick={cancelClearCart}
              className="flex-1 px-3 py-2 bg-gray-200 text-gray-800 text-sm rounded-lg hover:bg-gray-300 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2 mb-3">
          <button
            onClick={handleClearCart}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-red-600 border hover:border-red-200 rounded-lg transition-colors">
            <Trash2 className="w-4 h-4" />
            Clear Cart
          </button>
          <Link
            href="/cart"
            onClick={onOrderNowClick}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center group">
            Order Now
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      )}
    </div>
  );
}
